import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CheckCircle2, Clock3, FileText, RotateCw, XCircle } from "lucide-react";
import { toast } from "react-toastify";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  useGetBusinessReportsQuery,
  useGetSnapshotGridRowsQuery,
  useRetrySnapshotCollectionFailedResultsMutation,
} from "@/modules/snapshots/businessReports.api";
import { useGetSubcollectionsQuery } from "@/modules/snapshots/snapshotCollection.api";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { useSnapshotPermission } from "@/modules/snapshots/hooks/useSnapshotPermission";
import { buildSnapshotGridPath } from "@/modules/snapshots/routes";
import { SubcollectionDropdown } from "../breadcrumb/SubcollectionDropdown";

import { SnapshotCollectionsOutcomesExport } from "./exportOutcomes";
import { SnapshotCollectionsOutcomesList } from "./list";

interface Props {
  selectedCollection: SnapshotCollection;
}

interface SnapshotStatusSummary {
  completed: number;
  failed: number;
  processing: number;
  total: number;
}

export const SnapshotCollectionsOutcomes: React.FC<Props> = ({ selectedCollection }) => {
  const { data: businessReports = [] } = useGetBusinessReportsQuery();
  const businessReport = businessReports.find(r => r.id === selectedCollection.business_report_id);
  const isOwner = useSnapshotPermission(selectedCollection);
  const [hasFailedResults, setHasFailedResults] = React.useState(false);
  const [retryFailedResults, { isLoading: isRetryingFailedResults }] =
    useRetrySnapshotCollectionFailedResultsMutation();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const subcollectionId = searchParams.get("subcollection_id") ?? undefined;
  const statusSummaryPayload = React.useMemo(
    () => ({
      offset: 0,
      limit: 1,
      query: "",
      ...(subcollectionId ? { subcollection_id: subcollectionId } : {}),
      ordering: { order_by: "status", descending: false },
      column_filters: {},
    }),
    [subcollectionId],
  );
  const { data: statusSummaryData, isFetching: isFetchingStatusSummary } = useGetSnapshotGridRowsQuery(
    {
      snapshotCollectionId: selectedCollection.id,
      payload: statusSummaryPayload,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: subcollections = [] } = useGetSubcollectionsQuery({
    snapshot_collection_id: selectedCollection.id,
  });

  const activeSubcollection = subcollectionId ? subcollections.find(s => s.id === subcollectionId) : undefined;
  const activeSubcollectionName = subcollectionId ? (activeSubcollection?.name ?? "Unknown") : "All";

  React.useEffect(() => {
    setHasFailedResults(false);
  }, [selectedCollection.id, subcollectionId]);

  const handleFailedResultsAvailabilityChange = React.useCallback((nextHasFailedResults: boolean) => {
    setHasFailedResults(nextHasFailedResults);
  }, []);

  const clearSubcollection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("subcollection_id");
    const search = next.toString();
    navigate({ search: search ? `?${search}` : "" });
  };
  const gridLink = buildSnapshotGridPath(selectedCollection.business_report_id, selectedCollection.id, {
    subcollectionId,
  });
  const statusSummary = React.useMemo<SnapshotStatusSummary>(
    () => ({
      completed: statusSummaryData?.status_summary.completed ?? 0,
      failed: statusSummaryData?.status_summary.failed ?? 0,
      processing: statusSummaryData?.status_summary.processing ?? 0,
      total: statusSummaryData?.status_summary.total ?? selectedCollection.snapshots,
    }),
    [selectedCollection.snapshots, statusSummaryData?.status_summary],
  );
  const canRetryFailures =
    isOwner && (hasFailedResults || statusSummaryData?.status_summary.has_failed_results || statusSummary.failed > 0);

  const handleRetryFailedResults = React.useCallback(async () => {
    try {
      const result = await retryFailedResults({ snapshotCollectionId: selectedCollection.id }).unwrap();
      if (result.retried_results > 0) {
        toast.success(
          `Retrying ${result.retried_results} failed field${result.retried_results === 1 ? "" : "s"} across ${result.affected_outcomes} snapshot${result.affected_outcomes === 1 ? "" : "s"}`,
        );
      } else {
        toast.info("No failed fields to retry");
      }
    } catch {
      toast.error("Failed to retry failed fields");
    }
  }, [retryFailedResults, selectedCollection.id]);

  return (
    <>
      <div className="mb-6">
        <h1 className="mb-2 text-2xl font-bold text-foreground">{businessReport?.name || "Template"}</h1>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/snapshots">Snapshots</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{businessReport?.name || "Template"}</BreadcrumbPage>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {subcollectionId ? (
                <BreadcrumbLink asChild>
                  <button type="button" onClick={clearSubcollection}>
                    {selectedCollection.name}
                  </button>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{selectedCollection.name}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <SubcollectionDropdown
                snapshotCollectionId={selectedCollection.id}
                isOwner={isOwner}
                trigger={
                  subcollectionId ? (
                    <button
                      type="button"
                      className="cursor-pointer rounded px-2 py-0.5 text-sm font-normal text-foreground hover:bg-muted"
                    >
                      {activeSubcollectionName}
                    </button>
                  ) : (
                    <button className="flex cursor-pointer flex-col items-start rounded px-2 py-0.5 text-left hover:bg-muted">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Sub-collection
                      </span>
                      <span className="text-sm font-normal text-foreground">{activeSubcollectionName}</span>
                    </button>
                  )
                }
              />
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <SnapshotCollectionStatusSummaryCards summary={statusSummary} isLoading={isFetchingStatusSummary} />

      <div className="mt-4 flex flex-wrap items-end gap-2">
        {selectedCollection && (
          <SnapshotCollectionsOutcomesExport
            snapshotCollection={selectedCollection}
            subcollectionId={subcollectionId}
            subcollectionName={activeSubcollection?.name}
          />
        )}
        {canRetryFailures && (
          <Button
            type="button"
            variant="second-white"
            className="gap-2"
            disabled={isRetryingFailedResults}
            onClick={handleRetryFailedResults}
          >
            <RotateCw className="h-4 w-4" />
            {isRetryingFailedResults ? "Retrying..." : "Retry failures"}
          </Button>
        )}
        <Button asChild={true} variant="second-white">
          <Link to={gridLink}>Detailed View</Link>
        </Button>
      </div>

      <div className="mt-4 flow-root">
        <SnapshotCollectionsOutcomesList
          selectedCollection={selectedCollection}
          onFailedResultsAvailabilityChange={handleFailedResultsAvailabilityChange}
        />
      </div>
    </>
  );
};

interface SnapshotCollectionStatusSummaryCardsProps {
  isLoading: boolean;
  summary: SnapshotStatusSummary;
}

const SnapshotCollectionStatusSummaryCards: React.FC<SnapshotCollectionStatusSummaryCardsProps> = ({
  isLoading,
  summary,
}) => {
  const metrics = [
    {
      key: "total",
      label: "Total snapshots",
      value: summary.total,
      icon: FileText,
      toneClassName: "bg-slate-100 text-[#07142d]",
    },
    {
      key: "completed",
      label: "Successful",
      value: summary.completed,
      icon: CheckCircle2,
      toneClassName: "bg-emerald-50 text-emerald-700",
    },
    {
      key: "processing",
      label: "Processing",
      value: summary.processing,
      icon: Clock3,
      toneClassName: "bg-amber-50 text-amber-700",
    },
    {
      key: "failed",
      label: "Failed",
      value: summary.failed,
      icon: XCircle,
      toneClassName: "bg-red-50 text-red-700",
    },
  ] satisfies Array<{
    key: keyof SnapshotStatusSummary;
    label: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    toneClassName: string;
  }>;

  return (
    <section
      aria-label="Snapshot status summary"
      className="grid overflow-hidden rounded-lg border border-[#ddd5cb] bg-white md:grid-cols-4"
    >
      {metrics.map(metric => (
        <div
          key={metric.key}
          className="flex min-w-0 items-center gap-4 border-b border-[#ebe5de] px-5 py-4 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
        >
          <span
            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${metric.toneClassName}`}
          >
            <metric.icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-xs font-extrabold uppercase tracking-[0.04em] text-gray-500">{metric.label}</p>
            <p className="mt-1 text-2xl font-black leading-none text-[#07142d]">
              {isLoading && metric.key !== "total" ? "..." : metric.value}
            </p>
          </div>
        </div>
      ))}
    </section>
  );
};
